import React, { useState, useCallback, useRef, useEffect } from 'react';
import { X, Check, RotateCcw, ZoomIn, ZoomOut, Crop, Grid } from 'lucide-react';
import { logger } from '@/utils/logger';
import { safeCanvasToBlob } from '@/utils/compat';
import {
  DEFAULT_GRID_DIVISIONS,
  createGridSystem,
  FloorPlanGridMetadata
} from '@/utils/gridCoordinates';

/** Rectangle describing the crop selection in *natural* (source) image pixels. */
interface CropRegion {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Which gesture is currently in progress on the crop overlay. */
type DragMode = 'move' | 'resize-br' | 'resize-tl' | 'create';

interface FloorPlanCropModalProps {
  isOpen: boolean;
  imageFile: File;
  onClose: () => void;
  onConfirm: (croppedImage: Blob, metadata: FloorPlanGridMetadata) => void;
}

const FloorPlanCropModal: React.FC<FloorPlanCropModalProps> = ({
  isOpen,
  imageFile,
  onClose,
  onConfirm
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [imageUrl, setImageUrl] = useState<string>('');
  const [imageLoaded, setImageLoaded] = useState(false);
  const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 });
  const [displaySize, setDisplaySize] = useState({ width: 0, height: 0 });
  const [scale, setScale] = useState(1);

  // Crop state
  const [cropRegion, setCropRegion] = useState<CropRegion | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [dragMode, setDragMode] = useState<DragMode>('create');

  // Grid settings
  const [gridDivisions, setGridDivisions] = useState(DEFAULT_GRID_DIVISIONS);
  const [showGrid, setShowGrid] = useState(true);

  // Zoom state (prepared for future implementation)
  const [_viewScale, _setViewScale] = useState(1);

  // Load image when file changes
  useEffect(() => {
    if (imageFile) {
      const url = URL.createObjectURL(imageFile);
      setImageUrl(url);
      setImageLoaded(false);
      setCropRegion(null);

      return () => URL.revokeObjectURL(url);
    }
  }, [imageFile]);

  // Calculate display size when image loads or container resizes
  useEffect(() => {
    if (!imageLoaded || !containerRef.current || naturalSize.width === 0) return;

    const updateDisplaySize = () => {
      const container = containerRef.current;
      if (!container) return;

      const containerWidth = container.clientWidth - 40; // padding
      const containerHeight = container.clientHeight - 40;

      const imageAspect = naturalSize.width / naturalSize.height;
      const containerAspect = containerWidth / containerHeight;

      let displayW: number;
      let displayH: number;
      if (imageAspect > containerAspect) {
        displayW = containerWidth;
        displayH = containerWidth / imageAspect;
      } else {
        displayH = containerHeight;
        displayW = containerHeight * imageAspect;
      }

      setDisplaySize({ width: displayW, height: displayH });
      setScale(displayW / naturalSize.width);
    };

    updateDisplaySize();
    window.addEventListener('resize', updateDisplaySize);
    return () => window.removeEventListener('resize', updateDisplaySize);
  }, [imageLoaded, naturalSize]);

  // Handle image load
  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    setNaturalSize({ width: img.naturalWidth, height: img.naturalHeight });
    setImageLoaded(true);

    // Default crop region: full image
    setCropRegion({
      x: 0,
      y: 0,
      width: img.naturalWidth,
      height: img.naturalHeight
    });

    logger.info('Floor plan image loaded', {
      naturalWidth: img.naturalWidth,
      naturalHeight: img.naturalHeight
    });
  };

  // Convert screen coordinates to image coordinates
  const screenToImage = useCallback((screenX: number, screenY: number) => {
    if (!containerRef.current) return { x: 0, y: 0 };

    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const canvasRect = canvas.getBoundingClientRect();

    const x = (screenX - canvasRect.left) / scale;
    const y = (screenY - canvasRect.top) / scale;

    return {
      x: Math.max(0, Math.min(naturalSize.width, x)),
      y: Math.max(0, Math.min(naturalSize.height, y))
    };
  }, [scale, naturalSize]);

  // Mouse/touch event handlers
  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    const { x, y } = screenToImage(e.clientX, e.clientY);

    setIsDragging(true);
    setDragStart({ x, y });

    if (cropRegion) {
      // Check if clicking on resize handles or inside crop area
      const margin = 20 / scale;
      const isInsideCrop = (
        x >= cropRegion.x && x <= cropRegion.x + cropRegion.width &&
        y >= cropRegion.y && y <= cropRegion.y + cropRegion.height
      );

      const isNearBR = (
        Math.abs(x - (cropRegion.x + cropRegion.width)) < margin &&
        Math.abs(y - (cropRegion.y + cropRegion.height)) < margin
      );

      const isNearTL = (
        Math.abs(x - cropRegion.x) < margin &&
        Math.abs(y - cropRegion.y) < margin
      );

      if (isNearBR) {
        setDragMode('resize-br');
      } else if (isNearTL) {
        setDragMode('resize-tl');
      } else if (isInsideCrop) {
        setDragMode('move');
      } else {
        setDragMode('create');
        setCropRegion({ x, y, width: 0, height: 0 });
      }
    } else {
      setDragMode('create');
      setCropRegion({ x, y, width: 0, height: 0 });
    }
  }, [cropRegion, scale, screenToImage]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDragging || !cropRegion) return;

    const { x, y } = screenToImage(e.clientX, e.clientY);

    if (dragMode === 'create' || dragMode === 'resize-br') {
      const newWidth = Math.max(50, x - (dragMode === 'create' ? dragStart.x : cropRegion.x));
      const newHeight = Math.max(50, y - (dragMode === 'create' ? dragStart.y : cropRegion.y));

      setCropRegion(prev => prev ? {
        ...prev,
        x: dragMode === 'create' ? dragStart.x : prev.x,
        y: dragMode === 'create' ? dragStart.y : prev.y,
        width: Math.min(newWidth, naturalSize.width - (dragMode === 'create' ? dragStart.x : prev.x)),
        height: Math.min(newHeight, naturalSize.height - (dragMode === 'create' ? dragStart.y : prev.y))
      } : null);
    } else if (dragMode === 'resize-tl') {
      const newX = Math.max(0, Math.min(x, cropRegion.x + cropRegion.width - 50));
      const newY = Math.max(0, Math.min(y, cropRegion.y + cropRegion.height - 50));

      setCropRegion(prev => prev ? {
        x: newX,
        y: newY,
        width: prev.x + prev.width - newX,
        height: prev.y + prev.height - newY
      } : null);
    } else if (dragMode === 'move') {
      const dx = x - dragStart.x;
      const dy = y - dragStart.y;

      setCropRegion(prev => prev ? {
        ...prev,
        x: Math.max(0, Math.min(prev.x + dx, naturalSize.width - prev.width)),
        y: Math.max(0, Math.min(prev.y + dy, naturalSize.height - prev.height))
      } : null);

      setDragStart({ x, y });
    }
  }, [isDragging, cropRegion, dragMode, dragStart, naturalSize, screenToImage]);

  const handlePointerUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Reset crop to full image
  const handleReset = useCallback(() => {
    setCropRegion({
      x: 0,
      y: 0,
      width: naturalSize.width,
      height: naturalSize.height
    });
  }, [naturalSize]);

  // Confirm and crop
  const handleConfirm = useCallback(async () => {
    if (!cropRegion || !imageUrl) return;

    try {
      // Load image for cropping
      const img = new Image();
      img.crossOrigin = 'anonymous';

      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = reject;
        img.src = imageUrl;
      });

      // Create canvas for cropping
      const canvas = document.createElement('canvas');
      canvas.width = cropRegion.width;
      canvas.height = cropRegion.height;

      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Canvas context not available');

      // Draw cropped region
      ctx.drawImage(
        img,
        cropRegion.x, cropRegion.y, cropRegion.width, cropRegion.height,
        0, 0, cropRegion.width, cropRegion.height
      );

      // Convert to blob. Use the cross-browser helper: iOS Safari can invoke
      // the native toBlob callback with null on large canvases (a full-res
      // floor plan), which the old inline Promise turned into a hard reject.
      const blob = await safeCanvasToBlob(canvas, 'image/jpeg', 0.92);
      if (!blob) throw new Error('Failed to create blob');

      // Create grid system for the cropped image (used for metadata)
      createGridSystem(cropRegion.width, cropRegion.height, gridDivisions);

      // Create metadata
      const metadata: FloorPlanGridMetadata = {
        originalWidth: naturalSize.width,
        originalHeight: naturalSize.height,
        cropRegion: cropRegion,
        gridDivisions,
        aspectRatio: cropRegion.width / cropRegion.height
      };

      logger.info('Floor plan cropped', {
        originalSize: naturalSize,
        croppedSize: { width: cropRegion.width, height: cropRegion.height },
        gridDivisions,
        aspectRatio: metadata.aspectRatio
      });

      onConfirm(blob, metadata);
    } catch (error) {
      logger.error('Failed to crop floor plan', { error });
    }
  }, [cropRegion, imageUrl, naturalSize, gridDivisions, onConfirm]);

  // Draw canvas with crop overlay and grid
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !imageLoaded) return;

    canvas.width = displaySize.width;
    canvas.height = displaySize.height;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Load and draw image
    const img = new Image();
    img.onload = () => {
      // Draw full image (dimmed)
      ctx.globalAlpha = 0.4;
      ctx.drawImage(img, 0, 0, displaySize.width, displaySize.height);
      ctx.globalAlpha = 1;

      if (cropRegion) {
        // Draw bright crop region
        const cropX = cropRegion.x * scale;
        const cropY = cropRegion.y * scale;
        const cropW = cropRegion.width * scale;
        const cropH = cropRegion.height * scale;

        // Clip and draw bright version
        ctx.save();
        ctx.beginPath();
        ctx.rect(cropX, cropY, cropW, cropH);
        ctx.clip();
        ctx.drawImage(img, 0, 0, displaySize.width, displaySize.height);
        ctx.restore();

        // Draw grid overlay if enabled
        if (showGrid && cropW > 0 && cropH > 0) {
          ctx.strokeStyle = 'rgba(255, 215, 0, 0.3)';
          ctx.lineWidth = 1;

          const cellW = cropW / gridDivisions;
          const cellH = cropH / gridDivisions;

          for (let i = 1; i < gridDivisions; i++) {
            // Vertical lines
            ctx.beginPath();
            ctx.moveTo(cropX + i * cellW, cropY);
            ctx.lineTo(cropX + i * cellW, cropY + cropH);
            ctx.stroke();

            // Horizontal lines
            ctx.beginPath();
            ctx.moveTo(cropX, cropY + i * cellH);
            ctx.lineTo(cropX + cropW, cropY + i * cellH);
            ctx.stroke();
          }
        }

        // Draw crop border
        ctx.strokeStyle = '#FFD700';
        ctx.lineWidth = 2;
        ctx.strokeRect(cropX, cropY, cropW, cropH);

        // Draw corner handles
        const handleSize = 12;
        ctx.fillStyle = '#FFD700';

        // Top-left
        ctx.fillRect(cropX - handleSize/2, cropY - handleSize/2, handleSize, handleSize);
        // Bottom-right
        ctx.fillRect(cropX + cropW - handleSize/2, cropY + cropH - handleSize/2, handleSize, handleSize);
        // Top-right
        ctx.fillRect(cropX + cropW - handleSize/2, cropY - handleSize/2, handleSize, handleSize);
        // Bottom-left
        ctx.fillRect(cropX - handleSize/2, cropY + cropH - handleSize/2, handleSize, handleSize);
      }
    };
    img.src = imageUrl;
  }, [imageUrl, imageLoaded, displaySize, scale, cropRegion, showGrid, gridDivisions]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[400] bg-black/95 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-yellow-400/20 border-b border-yellow-400">
        <h2 className="text-xl font-bold text-yellow-400">Crop Floor Plan</h2>
        <button
          onClick={onClose}
          className="p-2 text-yellow-400 hover:text-white transition-colors"
        >
          <X size={24} />
        </button>
      </div>

      {/* Instructions */}
      <div className="p-3 bg-black/50 border-b border-yellow-400/50">
        <p className="text-yellow-400 text-sm">
          Drag to select the area you want to use. The grid will help with precise pin placement.
        </p>
      </div>

      {/* Canvas Container */}
      <div
        ref={containerRef}
        className="flex-1 relative overflow-hidden flex items-center justify-center p-5"
      >
        {!imageLoaded && (
          <div className="text-yellow-400">Loading image...</div>
        )}

        <canvas
          ref={canvasRef}
          className="cursor-crosshair touch-none"
          style={{
            display: imageLoaded ? 'block' : 'none',
            maxWidth: '100%',
            maxHeight: '100%'
          }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerUp}
        />

        {/* Hidden image for loading */}
        <img
          src={imageUrl}
          alt="Floor plan"
          onLoad={handleImageLoad}
          style={{ display: 'none' }}
        />
      </div>

      {/* Controls */}
      <div className="p-4 bg-black/50 border-t border-yellow-400/50">
        {/* Grid Settings */}
        <div className="mb-4 flex items-center gap-4">
          <button
            onClick={() => setShowGrid(!showGrid)}
            className={`flex items-center gap-2 px-3 py-2 rounded ${
              showGrid ? 'bg-yellow-400 text-black' : 'bg-gray-700 text-white'
            }`}
          >
            <Grid size={18} />
            <span className="text-sm">Grid</span>
          </button>

          <div className="flex items-center gap-2">
            <label className="text-yellow-400 text-sm">Divisions:</label>
            <select
              value={gridDivisions}
              onChange={(e) => setGridDivisions(Number(e.target.value))}
              className="bg-black/50 border border-yellow-400 text-yellow-400 rounded px-2 py-1 text-sm"
            >
              <option value={12}>12x12</option>
              <option value={24}>24x24 (Recommended)</option>
              <option value={36}>36x36</option>
              <option value={48}>48x48</option>
            </select>
          </div>
        </div>

        {/* Crop Info */}
        {cropRegion && (
          <div className="mb-4 text-yellow-400 text-sm">
            <span>Crop: {Math.round(cropRegion.width)} x {Math.round(cropRegion.height)} px</span>
            <span className="ml-4">Aspect: {(cropRegion.width / cropRegion.height).toFixed(2)}</span>
            <span className="ml-4">Grid cells: {gridDivisions * gridDivisions}</span>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center justify-between">
          <button
            onClick={handleReset}
            className="flex items-center gap-2 px-4 py-2 bg-gray-700 text-white rounded hover:bg-gray-600"
          >
            <RotateCcw size={18} />
            Reset
          </button>

          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-700 text-white rounded hover:bg-gray-600"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={!cropRegion || cropRegion.width < 50 || cropRegion.height < 50}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-500 disabled:bg-gray-600 disabled:cursor-not-allowed"
            >
              <Check size={18} />
              Apply Crop
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FloorPlanCropModal;
